import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import DayPicker from 'react-day-picker'

import { FaIcon } from '../index'
import { getDateOnlyUnixFormat } from '../../date_time_format'

const AddWeeklyRounds = ({
  details,
  setDetails,
  selectedWeekDays,
  setSelectedWeekDays,
  givenDays,
  setGivenDays,
  startDate,
  setStartDate,
  numberOfWeeks,
  setNumberOfWeeks,
  dayRoundNumber,
  setDayRoundNumber,
  numberOfExistingRounds,
  customerDateFormat,
}) => {
  // Falls back to MM/DD/YYYY when no customer format is threaded down
  const DATE_INPUT_FORMAT = getDateOnlyUnixFormat(customerDateFormat)

  const pickerRef = useRef(null)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [dateInputValue, setDateInputValue] = useState(startDate ? moment(startDate).format(DATE_INPUT_FORMAT) : '')

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    let roundsToAdd = []

    if (givenDays.length > 0 && startDate) {
      let formatedStartDate = moment(startDate)
      let endDate = formatedStartDate.clone().add((numberOfWeeks ?? 1) - 1, 'weeks').toDate()
      givenDays.map(day => {
        let tmp = formatedStartDate.clone().day(day)

        if( tmp.isSameOrAfter(formatedStartDate, 'd') ){
          roundsToAdd.push(moment(tmp).toDate())
        }
        while( tmp.isBefore(endDate) ){
          roundsToAdd.push(moment(tmp.add(7, 'days')).toDate())
        }
      })
    }

    if (numberOfExistingRounds > 0) {
      const remainingSlots = numberOfExistingRounds - (details.manuallyRounds || []).length
      roundsToAdd = roundsToAdd.slice(0, Math.max(0, remainingSlots))
    }

    const mergedRounds = [ ...details.manuallyRounds, ...roundsToAdd ]
    const newDates = {}
    mergedRounds.forEach(round => {
      const key = String(moment(round).format('ddd MMM DD YYYY'))
      newDates[key] = (newDates[key] || 0) + 1
    })
    setNumberOfWeeks(numberOfWeeks)
    setDayRoundNumber(newDates)

    setDetails({
      ...details,
      weeklyRounds: [ ...roundsToAdd ],
      rounds: [ ...details.manuallyRounds, ...roundsToAdd],
    })
  }, [numberOfWeeks, givenDays, startDate])

  const handleSelectDay = (day) => {
    let selected = false
    if (selectedWeekDays[day][1] === false)
      selected = true

    setSelectedWeekDays({ ...selectedWeekDays, [day] : [selectedWeekDays[day][0], selected] })
    if (selected)
      setGivenDays([...givenDays, selectedWeekDays[day][0]])
    else
      setGivenDays(givenDays.filter(item => item !== selectedWeekDays[day][0]))
  }

  useEffect(() => {
    setDateInputValue(startDate ? moment(startDate).format(DATE_INPUT_FORMAT) : '')
  }, [ startDate ])

  const handleDayChange = (selectedDay, { disabled }) => {
    if (disabled || moment(selectedDay).isBefore(moment(), 'day'))
      return

    setStartDate(selectedDay)
    setIsPickerOpen(false)
  }

  const handleDateInputChange = (e) => {
    const typedValue = e.target.value
    setDateInputValue(typedValue)

    const parsedDate = moment(typedValue, DATE_INPUT_FORMAT, true)
    if (parsedDate.isValid() && !parsedDate.isBefore(moment(), 'day')) {
      setStartDate(parsedDate.toDate())
      setIsPickerOpen(false)
    }
  }

  const handleDateInputBlur = () => {
    const parsedDate = moment(dateInputValue, DATE_INPUT_FORMAT, true)
    if (!parsedDate.isValid() || parsedDate.isBefore(moment(), 'day')) {
      setDateInputValue(startDate ? moment(startDate).format(DATE_INPUT_FORMAT) : '')
    }
  }

  const handleWeekChange = (weeksNumber) => {
    clearTimeout(window._typingTimer)
    const maxWeeks = numberOfExistingRounds > 0 ? Math.min(52, numberOfExistingRounds) : 52
    if (Number(weeksNumber) >= 0 && Number(weeksNumber) <= maxWeeks) {
      window._typingTimer = setTimeout(() => {
        setNumberOfWeeks(weeksNumber)
      }, 500)
    }
  }

  const handleStartDateInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setIsPickerOpen(false)
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIsPickerOpen(true)
    }
  }

  const togglePicker = () => setIsPickerOpen(!isPickerOpen)

  const handleFieldMouseDown = (e) => {
    if (e.target.tagName !== 'INPUT') {
      e.preventDefault()
    }
  }

  const handleFieldBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsPickerOpen(false)
    }
  }

  const handleFieldKeyDown = (e) => {
    if (e.key === 'Escape' && isPickerOpen) {
      e.preventDefault()
      e.nativeEvent.stopImmediatePropagation()
      setIsPickerOpen(false)
    }
  }

  return (
    <div className='weekly_rounds_container'>
      <p className='content_information rounds_type_info'>
        { window.I18n.t('shared.components.schedule_rounds.add_weekly_rounds_info') }
      </p>
      <p className='weekly_rounds_label'>
        { window.I18n.t('shared.components.schedule_rounds.which_day_to_play') }
      </p>
      <span className='weekly_rounds_days_container'>
        { Object.keys(selectedWeekDays).map(day => {
          return (
            <span
              className={`day_of_week ${selectedWeekDays[day][1] ? 'selected' : ''}`}
              onClick={() => handleSelectDay(day)}
            >
              { day }
            </span>
          )})
        }
      </span>
      <p className='weekly_rounds_label add-margin-top-10'>
        { window.I18n.t('shared.components.schedule_rounds.when_round_start') }
      </p>
      <div
        className='weekly_rounds_date_picker'
        ref={ pickerRef }
        onBlur={ handleFieldBlur }
        onKeyDown={ handleFieldKeyDown }
      >
        <span className='weekly_rounds_input day_picker_input' onMouseDown={ handleFieldMouseDown } onClick={ togglePicker }>
          <input
            className='start_date_input'
            value={ dateInputValue }
            onChange={ handleDateInputChange }
            onBlur={ handleDateInputBlur }
            onKeyDown={ handleStartDateInputKeyDown }
            placeholder={ window.I18n.t('shared.components.schedule_rounds.select_date_placeholder') }
            aria-label={ window.I18n.t('shared.components.schedule_rounds.when_round_start') }
          />
          <FaIcon icon='calendar-days' />
        </span>
        { isPickerOpen && (
          <DayPicker
            className='weekly_rounds_picker'
            selectedDays={ startDate }
            onDayClick={ handleDayChange }
            disabledDays={
              numberOfExistingRounds && numberOfExistingRounds === details.rounds.length
                ? [ { before: new Date() }, { after: new Date() }, new Date() ]
                : { before: new Date() }
            }
          />
        )}
      </div>
      <p className='weekly_rounds_label how_many_weeks'>
        { window.I18n.t('shared.components.schedule_rounds.how_many_weeks') }
      </p>
      <input
        placeholder='e.g. 20'
        id='number_of_weeks'
        className='weekly_rounds_input'
        value={ numberOfWeeks }
        onChange={(e) => handleWeekChange(e.target.value) }
        type='text'
        inputMode='numeric'
      />
      <p className='weekly_rounds_info'>
        { !window.I18n ? '' : window.I18n.t('shared.components.schedule_rounds.max_rounds', { count: numberOfExistingRounds > 0 ? numberOfExistingRounds : 52 }) }
      </p>
    </div>
  )
}

AddWeeklyRounds.propTypes = {
  details: PropTypes.object.isRequired,
  setDetails: PropTypes.func.isRequired,
  selectedWeekDays: PropTypes.object.isRequired,
  setSelectedWeekDays: PropTypes.func.isRequired,
  givenDays: PropTypes.array,
  setGivenDays: PropTypes.func.isRequired,
  startDate: PropTypes.instanceOf(Date),
  setStartDate: PropTypes.func.isRequired,
  numberOfWeeks: PropTypes.string,
  setNumberOfWeeks: PropTypes.func.isRequired,
  dayRoundNumber: PropTypes.object.isRequired,
  setDayRoundNumber: PropTypes.func.isRequired,
  numberOfExistingRounds: PropTypes.number,
  customerDateFormat: PropTypes.string,
}

export default AddWeeklyRounds
